<template>
  <main class="sso-page">
    <div class="sso-backdrop" aria-hidden="true"></div>

    <div class="sso-shell">
      <header class="sso-brand">
        <img src="@/assets/image/login/logoBlue.png" alt="Graphchain" />
      </header>

      <section class="sso-card" aria-live="polite">
        <div v-if="status === 'loading'" class="sso-state sso-state--loading">
          <div class="sso-spinner-wrap" aria-hidden="true">
            <span class="sso-spinner"></span>
          </div>
          <h1>正在进入站点</h1>
          <p>正在获取访问凭证，请稍候</p>
          <div class="sso-progress" aria-hidden="true">
            <span></span>
          </div>
        </div>

        <div v-else class="sso-state sso-state--error">
          <div class="sso-error-icon" aria-hidden="true">
            <i class="el-icon-warning-outline"></i>
          </div>
          <h1>无法进入站点</h1>
          <!-- <p>{{ errorMessage }}</p> -->
          <div class="sso-actions">
            <el-button
              type="primary"
              :loading="retrying"
              icon="el-icon-refresh-right"
              @click="retry"
            >
              重新尝试
            </el-button>
            <el-button icon="el-icon-arrow-left" @click="goHome">
              返回图链
            </el-button>
          </div>
        </div>
      </section>
    </div>
  </main>
</template>

<script>
import { getSiteAuthAccessKey } from "@/aips/sso";
import { isGoogleAuthPending, saveRedirectUrl } from "@/utils/storageHelper";

export default {
  name: "SsoAuthorize",
  data() {
    return {
      status: "loading",
      errorMessage: "",
      retrying: false,
      authorizing: false,
    };
  },
  created() {
    this.startAuthorize();
  },
  methods: {
    getSiteId() {
      const siteId = this.$route.query.siteId;
      return typeof siteId === "string" ? siteId.trim() : "";
    },
    saveLoginReturnRoute(siteId) {
      saveRedirectUrl({
        path: "/sso",
        query: { siteId },
      });
    },
    redirectToLogin(siteId) {
      this.saveLoginReturnRoute(siteId);
      this.$router.replace("/login");
    },
    getRedirectUrl(response) {
      const redirectUrl =
        response && response.data && response.data.redirectUrl;
      if (!redirectUrl || typeof redirectUrl !== "string") {
        return "";
      }

      try {
        const target = new URL(redirectUrl);
        const allowsHttp = window.location.protocol === "http:";
        const validProtocol =
          target.protocol === "https:" ||
          (allowsHttp && target.protocol === "http:");
        if (!validProtocol || target.username || target.password) {
          return "";
        }
        return target.toString();
      } catch (e) {
        return "";
      }
    },
    async startAuthorize() {
      if (this.authorizing) return;

      const siteId = this.getSiteId();
      if (!siteId || !/^[A-Za-z0-9_-]{1,64}$/.test(siteId)) {
        this.showError("站点参数无效，请从原站点重新进入");
        return;
      }

      const hasCompletedLogin =
        !!window.localStorage.getItem("t_k") && !isGoogleAuthPending();
      if (!hasCompletedLogin) {
        this.redirectToLogin(siteId);
        return;
      }

      this.authorizing = true;
      this.status = "loading";
      this.errorMessage = "";
      try {
        const response = await getSiteAuthAccessKey(siteId);
        if (!response || response.code !== 200) {
          this.showError(
            (response && (response.message || response.msg)) ||
              "授权失败，请从原站点重新进入",
          );
          return;
        }

        const redirectUrl = this.getRedirectUrl(response);
        if (!redirectUrl) {
          this.showError("后端未返回有效的站点地址");
          return;
        }

        window.location.replace(redirectUrl);
      } catch (e) {
        if (e && e.response && e.response.status === 401) {
          this.saveLoginReturnRoute(siteId);
          return;
        }
        this.showError((e && e.message) || "网络异常，请稍后重新尝试");
      } finally {
        this.authorizing = false;
      }
    },
    showError(message) {
      this.status = "error";
      this.errorMessage = message;
    },
    async retry() {
      this.retrying = true;
      await this.startAuthorize();
      this.retrying = false;
    },
    goHome() {
      this.$router.replace("/home");
    },
  },
};
</script>

<style lang="less" scoped>
@primary: #3b82f6;
@page-bg: #050505;
@card-bg: rgba(37, 39, 64, 0.85);
@card-border: rgba(215, 225, 255, 0.09);
@text-main: rgba(255, 255, 255, 0.95);
@text-muted: #afb1cc;

@keyframes sso-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes sso-progress {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(260%);
  }
}

.sso-page {
  min-height: 100vh;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 24px;
  color: @text-main;
  font-family: "General Sans", "PingFang SC", system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
  background: @page-bg;
}

.sso-backdrop {
  position: fixed;
  inset: 0;
  pointer-events: none;
  background: #050505;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(2, 2, 5, 0.54);
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle, #a2b1ff 1px, transparent 1px);
    background-size: 24px 24px;
    opacity: 0.12;
  }
}

.sso-shell {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 420px;
}

.sso-card {
  position: relative;
  width: 100%;
  box-sizing: border-box;
  padding: 40px 32px;
  border: 1px solid @card-border;
  border-radius: 20px;
  background: @card-bg;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.32),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(40px);
  -webkit-backdrop-filter: blur(40px);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    border-radius: inherit;
    pointer-events: none;
    background: linear-gradient(
      to bottom,
      rgba(59, 130, 246, 0.1),
      transparent 55%
    );
  }
}

.sso-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 26px;

  img {
    display: block;
    width: 220px;
    max-width: 76%;
  }
}

.sso-state {
  text-align: center;
}

.sso-spinner-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  box-sizing: border-box;
  margin: 0 auto 24px;
  border: 1px solid rgba(96, 165, 250, 0.24);
  border-radius: 16px;
  background: rgba(59, 130, 246, 0.12);
}

.sso-spinner {
  display: block;
  width: 27px;
  height: 27px;
  box-sizing: border-box;
  border: 2.5px solid rgba(255, 255, 255, 0.16);
  border-top-color: @primary;
  border-radius: 50%;
  animation: sso-spin 0.75s linear infinite;
}

h1 {
  margin: 0 0 10px;
  color: @text-main;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: 0;
}

p {
  max-width: 100%;
  margin: 0;
  color: @text-muted;
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}

.sso-progress {
  width: 180px;
  height: 4px;
  overflow: hidden;
  margin: 28px auto 0;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.1);

  span {
    display: block;
    width: 42%;
    height: 100%;
    border-radius: inherit;
    background: @primary;
    animation: sso-progress 1.5s ease-in-out infinite;
  }
}

.sso-error-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  margin: 0 auto 24px;
  border: 1px solid rgba(248, 113, 113, 0.34);
  border-radius: 50%;
  color: #f87171;
  font-size: 28px;
  background: rgba(220, 38, 38, 0.12);
}

.sso-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 30px;

  .el-button + .el-button {
    margin-left: 0;
  }

  .el-button {
    min-width: 136px;
    height: 42px;
    border-radius: 12px;
    font-family: inherit;
    font-size: 14px;
  }

  .el-button--primary {
    border-color: transparent;
    background: linear-gradient(to right, #6366f1, #2563eb);
    box-shadow: 0 8px 20px rgba(37, 99, 235, 0.22);
  }

  .el-button--primary:hover {
    border-color: transparent;
    background: linear-gradient(to right, #4f46e5, #1d4ed8);
  }

  .el-button:not(.el-button--primary) {
    border-color: rgba(227, 235, 255, 0.13);
    color: rgba(255, 255, 255, 0.72);
    background: rgba(29, 31, 51, 0.74);
  }

  .el-button:not(.el-button--primary):hover {
    border-color: rgba(59, 130, 246, 0.48);
    color: #fff;
    background: rgba(59, 130, 246, 0.14);
  }
}

@media (max-width: 480px) {
  .sso-page {
    padding: 16px;
  }

  .sso-card {
    padding: 36px 24px;
    border-radius: 18px;
  }

  .sso-brand {
    margin-bottom: 22px;

    img {
      width: 196px;
    }
  }

  .sso-actions {
    width: 100%;
    max-width: 280px;
    flex-direction: column;

    .el-button {
      width: 100%;
    }
  }
}
</style>
